import React from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { StepProps, MechanicalIssueType, MECHANICAL_ISSUE_LABELS } from '../../types';

const Container = styled.div`
  ${tw`flex flex-col h-full`}
`;

const SectionLabel = styled.div`
  ${tw`text-xs font-semibold text-text-secondary uppercase tracking-wider mb-3`}
`;

const Title = styled.h2`
  ${tw`text-xl font-semibold text-text-primary mb-2`}
`;

const Subtitle = styled.p`
  ${tw`text-sm text-text-secondary mb-6`}
`;

const OptionsContainer = styled.div`
  ${tw`flex flex-col gap-2 mb-6`}
`;

const CheckboxItem = styled.button<{ isSelected: boolean }>`
  ${tw`w-full p-4 text-left border rounded-lg transition-all flex items-center gap-3`}
  ${props =>
    props.isSelected
      ? tw`border-black bg-pink-50`
      : tw`border-border-default hover:border-border-default`}
`;

const Checkbox = styled.div<{ isSelected: boolean }>`
  ${tw`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0`}
  ${props => (props.isSelected ? tw`border-black bg-black` : tw`border-border-default`)}
`;

const CheckboxLabel = styled.span`
  ${tw`text-sm text-text-primary`}
`;

const OtherTextarea = styled.textarea`
  ${tw`w-full mt-3 p-3 border border-border-default rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black resize-none`}
  min-height: 100px;
`;

const ContinueButton = styled.button<{ disabled: boolean }>`
  ${tw`w-full py-4 font-semibold rounded-card mt-auto`}
  ${props =>
    props.disabled
      ? tw`bg-bg-secondary text-text-tertiary cursor-not-allowed`
      : tw`bg-black text-white hover:bg-black-hover`}
`;

const issueOptions = [
  MechanicalIssueType.CHECK_ENGINE_LIGHT,
  MechanicalIssueType.BRAKES_ISSUE,
  MechanicalIssueType.CLUTCH_ISSUE,
  MechanicalIssueType.BATTERY_ISSUE,
  MechanicalIssueType.BROKE_DOWN_NOT_DRIVEABLE,
  MechanicalIssueType.OTHER,
];

const MechanicalIssuesStep: React.FC<StepProps> = ({ formData, updateFormData, onNext }) => {
  const handleToggleIssue = (issue: MechanicalIssueType) => {
    const currentIssues = formData.mechanicalIssues;
    const isSelected = currentIssues.includes(issue);

    if (isSelected) {
      updateFormData({
        mechanicalIssues: currentIssues.filter(i => i !== issue),
      });
    } else {
      updateFormData({
        mechanicalIssues: [...currentIssues, issue],
      });
    }
  };

  const handleOtherDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    updateFormData({ mechanicalIssueOtherDescription: e.target.value });
  };

  const canContinue = formData.mechanicalIssues.length > 0;
  const showOtherInput = formData.mechanicalIssues.includes(MechanicalIssueType.OTHER);

  return (
    <Container>
      <SectionLabel>DAMAGE DETAILS</SectionLabel>
      <Title>What is the mechanical issue?</Title>
      <Subtitle>Select all that apply.</Subtitle>

      <OptionsContainer>
        {issueOptions.map(issue => (
          <React.Fragment key={issue}>
            <CheckboxItem
              isSelected={formData.mechanicalIssues.includes(issue)}
              onClick={() => handleToggleIssue(issue)}
            >
              <Checkbox isSelected={formData.mechanicalIssues.includes(issue)}>
                {formData.mechanicalIssues.includes(issue) && (
                  <FontAwesomeIcon icon={faCheck} size="xs" color="white" />
                )}
              </Checkbox>
              <CheckboxLabel>{MECHANICAL_ISSUE_LABELS[issue]}</CheckboxLabel>
            </CheckboxItem>
            {issue === MechanicalIssueType.OTHER && showOtherInput && (
              <OtherTextarea
                placeholder="Please describe the mechanical issue..."
                value={formData.mechanicalIssueOtherDescription || ''}
                onChange={handleOtherDescriptionChange}
              />
            )}
          </React.Fragment>
        ))}
      </OptionsContainer>

      <ContinueButton disabled={!canContinue} onClick={onNext}>
        Continue
      </ContinueButton>
    </Container>
  );
};

export default MechanicalIssuesStep;
